"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronUp, CreditCard, ExternalLink, FolderIcon, LogOut, Plus } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { SidebarLoungePoints } from "@/components/lounge/sidebar-points";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { Wordmark } from "@/components/ui/wordmark";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { ChatSidebarSkeleton } from "./chat-sidebar-skeleton";
import { SidebarChatSearch, SidebarShortcutKbd, useSidebarShortcut } from "./sidebar-actions";
import { StudioNav } from "./studio-nav";
import type { ChatProject } from "@/hooks/useChatProjects";
import type { Organization } from "@/lib/types";
interface ProjectsSidebarProps {
  projects: ChatProject[];
  selectedProjectId: string | null;
  onSelectProject: (projectId: string) => void;
  isLoading?: boolean;
  onCreateOpen: () => void;
  selectedOrganization: Organization | null;
  className?: string;
}
export function ProjectsSidebar(t0) {
  const $ = _c(75);
  const {
    projects,
    selectedProjectId,
    onSelectProject,
    isLoading,
    onCreateOpen,
    selectedOrganization,
    className
  } = t0;
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const isMac = useSidebarShortcut("j", onCreateOpen);
  let t1;
  if ($[0] !== queryClient || $[1] !== router || $[2] !== signOut) {
    t1 = async () => {
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://chat.llmgateway.io/login");
          }
        }
      });
    };
    $[0] = queryClient;
    $[1] = router;
    $[2] = signOut;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const logout = t1;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  if (isUserLoading) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <ChatSidebarSkeleton organization={null} isOrgLoading={false} />;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (!user) {
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Wordmark /></Link>;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="font-medium mb-2">Sign in required</div>;
      t4 = <p className="text-muted-foreground mb-3">Please sign in to view your projects.</p>;
      $[6] = t3;
      $[7] = t4;
    } else {
      t3 = $[6];
      t4 = $[7];
    }
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t2}<div className="w-full rounded-md border p-4 text-sm">{t3}{t4}<div className="flex items-center justify-end gap-2"><Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button></div></div></div><StudioNav /></SidebarHeader>;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== className) {
      t6 = <Sidebar className={className}>{t5}</Sidebar>;
      $[9] = className;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    return t6;
  }
  let t2;
  if ($[11] !== className) {
    t2 = cn(className, "max-md:hidden");
    $[11] = className;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SidebarMenuItem><SidebarMenuButton size="lg" asChild={true} tooltip="Lounge"><Link href="/" prefetch={true}><Wordmark size="sm" iconBox={true} /></Link></SidebarMenuButton></SidebarMenuItem>;
    t4 = <SidebarChatSearch disabled={true} />;
    $[13] = t3;
    $[14] = t4;
  } else {
    t3 = $[13];
    t4 = $[14];
  }
  let t5;
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Plus className="h-4 w-4" />;
    t6 = <span>New Project</span>;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  const t7 = isMac ? "\u2318J" : "Alt+J";
  let t8;
  if ($[17] !== t7) {
    t8 = <SidebarShortcutKbd keys={t7} />;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== onCreateOpen || $[20] !== t8) {
    t9 = <SidebarMenu>{t3}{t4}<SidebarMenuItem><SidebarMenuButton tooltip="New Project" className="border border-border" onClick={onCreateOpen}>{t5}{t6}{t8}</SidebarMenuButton></SidebarMenuItem></SidebarMenu>;
    $[19] = onCreateOpen;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <StudioNav />;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t9) {
    t11 = <SidebarHeader>{t9}{t10}</SidebarHeader>;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div><div className="mx-2 mb-2 border-t border-sidebar-border" /></div>;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">My Projects</div>;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== isLoading || $[28] !== onSelectProject || $[29] !== projects || $[30] !== selectedProjectId) {
    t14 = isLoading ? <div className="flex items-center justify-center py-8"><span className="text-muted-foreground text-sm">Loading projects...</span></div> : projects.length === 0 ? <div className="flex flex-col items-center justify-center py-8 px-4 text-center"><FolderIcon className="h-8 w-8 text-muted-foreground/50 mb-3" /><p className="text-sm text-muted-foreground">No projects yet</p></div> : <SidebarMenu className="px-2">{projects.map(project => <SidebarMenuItem key={project.id}><SidebarMenuButton isActive={selectedProjectId === project.id} onClick={() => onSelectProject(project.id)} tooltip={project.name} className="h-auto py-2"><div className="flex-1 min-w-0"><div className="truncate text-sm font-medium">{project.name}</div><div className="truncate text-xs text-muted-foreground">{project.fileCount}{" "}{project.fileCount === 1 ? "file" : "files"} ·{" "}{project.chatCount}{" "}{project.chatCount === 1 ? "chat" : "chats"}</div></div></SidebarMenuButton></SidebarMenuItem>)}</SidebarMenu>;
    $[27] = isLoading;
    $[28] = onSelectProject;
    $[29] = projects;
    $[30] = selectedProjectId;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== isHistoryHidden || $[33] !== t14) {
    t15 = <SidebarContent className="overflow-hidden pb-2">{t12}<div aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t13}{t14}</div></SidebarContent>;
    $[32] = isHistoryHidden;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SidebarLoungePoints />;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== selectedOrganization) {
    t17 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={selectedOrganization} isLoading={false} /></div>;
    $[36] = selectedOrganization;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const t18 = user.name ?? "User";
  let t19;
  if ($[38] !== user.name) {
    t19 = user.name?.split(" ").map(_temp).join("").toUpperCase().slice(0, 2) ?? "U";
    $[38] = user.name;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t19) {
    t20 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t19}</span></div>;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== user.name) {
    t21 = <span className="truncate font-semibold">{user.name}</span>;
    $[42] = user.name;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== user.email) {
    t22 = <span className="truncate text-xs text-muted-foreground">{user.email}</span>;
    $[44] = user.email;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t21 || $[47] !== t22) {
    t23 = <div className="grid flex-1 text-left text-sm leading-tight">{t21}{t22}</div>;
    $[46] = t21;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t18 || $[51] !== t20 || $[52] !== t23) {
    t25 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t18} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t20}{t23}{t24}</SidebarMenuButton></DropdownMenuTrigger>;
    $[50] = t18;
    $[51] = t20;
    $[52] = t23;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  let t27;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <DropdownMenuItem asChild={true}><Link href="/pricing" prefetch={true}><CreditCard className="mr-2 h-4 w-4" />{"Membership & Billing"}</Link></DropdownMenuItem>;
    t27 = <DropdownMenuSeparator />;
    $[54] = t26;
    $[55] = t27;
  } else {
    t26 = $[54];
    t27 = $[55];
  }
  let t28;
  let t29;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llmgateway.io/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t29 = <DropdownMenuSeparator />;
    $[56] = t28;
    $[57] = t29;
  } else {
    t28 = $[56];
    t29 = $[57];
  }
  let t30;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <span>Theme</span>;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  let t32;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <DropdownMenuItem className="justify-between gap-3" onSelect={_temp2}>{t30}<div onClick={_temp3} onKeyDown={_temp4}><ThemeToggle className="shrink-0" size="compact" /></div></DropdownMenuItem>;
    t32 = <DropdownMenuSeparator />;
    $[59] = t31;
    $[60] = t32;
  } else {
    t31 = $[59];
    t32 = $[60];
  }
  let t33;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <LogOut className="mr-2 h-4 w-4" />;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  let t34;
  if ($[62] !== logout) {
    t34 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t26}{t27}{t28}{t29}{t31}{t32}<DropdownMenuItem onClick={logout}>{t33}Log out</DropdownMenuItem></DropdownMenuContent>;
    $[62] = logout;
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  let t35;
  if ($[64] !== t25 || $[65] !== t34) {
    t35 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t25}{t34}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[64] = t25;
    $[65] = t34;
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  let t36;
  if ($[67] !== t17 || $[68] !== t35) {
    t36 = <SidebarFooter>{t16}{t17}{t35}</SidebarFooter>;
    $[67] = t17;
    $[68] = t35;
    $[69] = t36;
  } else {
    t36 = $[69];
  }
  let t37;
  if ($[70] !== t11 || $[71] !== t15 || $[72] !== t2 || $[73] !== t36) {
    t37 = <Sidebar collapsible="icon" className={t2}>{t11}{t15}{t36}</Sidebar>;
    $[70] = t11;
    $[71] = t15;
    $[72] = t2;
    $[73] = t36;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  return t37;
}
function _temp4(event_1) {
  return event_1.stopPropagation();
}
function _temp3(event_0) {
  return event_0.stopPropagation();
}
function _temp2(event) {
  return event.preventDefault();
}
function _temp(n) {
  return n[0];
}